<template>
    <div class="pos-page">
        <!-- Loader -->
        <div v-if="loading" class="pos-loader">
            <div class="loader"></div>
        </div>

        <!-- ESIR Header -->
        <div class="pos-header">
            <div class="ph-left">
                <select v-model="selectedEsirId" class="esir-select" @change="onEsirSelect">
                    <option value="">{{ $t('pos.selectEsir') }}</option>
                    <option v-for="e in esirs" :key="e.id" :value="e.id">
                        {{ e.locationName || e.esirCode }} {{ e.posNumber ? '(' + e.posNumber + ')' : '' }}
                    </option>
                </select>
                <span v-if="selectedEsir" class="esir-cert" :class="selectedEsir.hasCertificate ? 'cert-y' : 'cert-n'">
                    <i :class="selectedEsir.hasCertificate ? 'tim-icons icon-check-2' : 'tim-icons icon-alert-circle-exc'"></i>
                    {{ selectedEsir.hasCertificate ? $t('pos.certOk') : $t('pos.certMissing') }}
                </span>
            </div>
            <div class="ph-right">
                <button class="ph-btn" @click="showHistory = true" v-if="selectedEsirId">
                    <i class="tim-icons icon-paper"></i> {{ $t('pos.issuedInvoices') }}
                </button>
            </div>
        </div>

        <!-- No ESIR -->
        <div v-if="!selectedEsirId" class="pos-empty">
            <i class="tim-icons icon-tv-2"></i>
            <p>{{ $t('pos.selectEsir') }}</p>
        </div>

        <!-- POS Main -->
        <div v-else class="pos-main" :class="{ 'pos-main-full': invoiceType === 'Advance' }">

            <!-- LEFT: Artikli (sakrij za avans) -->
            <div class="pos-catalog" v-if="invoiceType !== 'Advance'">
                <div class="catalog-search">
                    <input v-model="itemSearch" :placeholder="$t('pos.searchItems')" class="cat-input">
                </div>
                <div class="catalog-grid">
                    <div v-for="item in filteredCatalog" :key="item.id"
                        class="cat-item" @click="addToReceipt(item)">
                        <div class="ci-name">{{ item.name }}</div>
                        <div class="ci-bottom">
                            <span class="ci-price">{{ item.price ? formatPrice(item.price) : '0,00' }}</span>
                            <span v-if="item.taxLabels" class="ci-tax">{{ item.taxLabels }}</span>
                            <span v-if="item.unit" class="ci-unit">/{{ item.unit }}</span>
                        </div>
                    </div>
                    <div v-if="filteredCatalog.length === 0" class="catalog-empty">
                        {{ $t('common.noData') }}
                    </div>
                </div>
            </div>

            <!-- RIGHT: Račun -->
            <div class="pos-receipt">
                <!-- Tip računa / transakcije -->
                <div class="rcpt-types">
                    <div class="rcpt-type-group">
                        <label>{{ $t('pos.invoiceType') }}</label>
                        <div class="type-btns">
                            <button v-for="t in invoiceTypes" :key="t.value"
                                :class="['tbtn', { active: invoiceType === t.value }]"
                                @click="invoiceType = t.value">{{ t.label }}</button>
                        </div>
                    </div>
                    <div class="rcpt-type-group">
                        <label>{{ $t('pos.transaction') }}</label>
                        <div class="type-btns">
                            <button :class="['tbtn', { active: transactionType === 'Sale' }]"
                                @click="transactionType = 'Sale'">{{ $t('pos.txn.sale') }}</button>
                            <button :class="['tbtn tbtn-ref', { active: transactionType === 'Refund' }]"
                                @click="transactionType = 'Refund'">{{ $t('pos.txn.refund') }}</button>
                        </div>
                    </div>
                </div>

                <!-- Aktivni avansi - dugme za otvaranje modala -->
                <div class="advances-bar" v-if="advanceChains.filter(c => c.status !== 'completed').length > 0 && invoiceType !== 'Advance'">
                    <button class="adv-open-btn" @click="showAdvancesModal = true">
                        <i class="tim-icons icon-bell-55"></i>
                        Avansi ({{ advanceChains.filter(c => c.status === 'active').length }} {{ $t('pos.advances.count') }}<span v-if="advanceChains.filter(c => c.status === 'refunded').length">, {{ advanceChains.filter(c => c.status === 'refunded').length }} {{ $t('pos.advances.forFinal') }}</span>)
                        <i class="tim-icons icon-minimal-right"></i>
                    </button>
                </div>

                <!-- PIB Kupca -->
                <div class="rcpt-buyer">
                    <label>{{ $t('pos.buyerId') }} <small>({{ $t('pos.buyerIdHint') }})</small></label>
                    <input v-model="buyerId" :placeholder="$t('pos.buyerIdPlaceholder')" class="buyer-input">
                </div>

                <!-- Referentni dokument -->
                <div class="rcpt-ref" v-if="transactionType === 'Refund' || refDocNumber">
                    <div class="ref-row">
                        <div class="ref-field">
                            <label>{{ $t('pos.refDocNumber') }} {{ transactionType === 'Refund' ? '*' : '' }}</label>
                            <input v-model="refDocNumber" placeholder="XXXXX-XXXXX-XXXXX" class="buyer-input">
                        </div>
                        <div class="ref-field ref-date">
                            <label>{{ $t('pos.refDocDate') }} {{ transactionType === 'Refund' ? '*' : '' }}</label>
                            <el-date-picker v-model="refDocDt" type="datetime" format="dd.MM.yyyy. HH:mm:ss"
                                :placeholder="$t('common.date')" size="small" style="width:100%"></el-date-picker>
                        </div>
                    </div>
                </div>

                <!-- Avans info panel -->
                <div class="advance-panel" v-if="invoiceType === 'Advance'">
                    <div class="adv-info">
                        <i class="tim-icons icon-bulb-63"></i>
                        <span>{{ $t('pos.advanceMode.info') }}</span>
                    </div>
                    <div class="adv-items">
                        <div class="adv-item" v-for="ai in advanceItems" :key="ai.label">
                            <span class="adv-label">{{ ai.name }}</span>
                            <input type="number" v-model.number="ai.amount" min="0" step="0.01"
                                placeholder="0.00" class="adv-input" @change="buildAdvanceReceipt()">
                        </div>
                    </div>
                    <div class="adv-desc">
                        <label>{{ $t('pos.advanceMode.description') }} * <small>({{ $t('pos.advanceMode.descriptionHint') }})</small></label>
                        <textarea v-model="advanceDescription" rows="2" class="buyer-input"
                            :placeholder="$t('pos.advanceMode.descriptionPlaceholder')"></textarea>
                    </div>
                </div>

                <!-- Stavke -->
                <div class="rcpt-items">
                    <table class="rcpt-table" v-if="receiptItems.length > 0">
                        <thead>
                            <tr>
                                <th class="th-name">{{ $t('pos.receipt.item') }}</th>
                                <th class="th-qty">{{ $t('pos.receipt.qty') }}</th>
                                <th class="th-price">{{ $t('pos.receipt.price') }}</th>
                                <th class="th-total">{{ $t('pos.receipt.itemTotal') }}</th>
                                <th class="th-act"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(ri, idx) in receiptItems" :key="idx">
                                <td class="td-name">
                                    <span>{{ ri.name }}</span>
                                    <small v-if="ri.label">{{ ri.label }} {{ ri.taxRate }}%</small>
                                </td>
                                <td class="td-qty">
                                    <div class="qty-wrap">
                                        <button class="qb" @click="changeQty(idx, -1)">−</button>
                                        <input type="number" v-model.number="ri.quantity"
                                            min="0.001" step="1" class="qi" @change="recalc()">
                                        <button class="qb" @click="changeQty(idx, 1)">+</button>
                                    </div>
                                </td>
                                <td class="td-price">
                                    <input type="number" v-model.number="ri.unitPrice"
                                        min="0" step="0.01" class="pi" @change="recalc()">
                                </td>
                                <td class="td-total">{{ formatPrice(ri.quantity * ri.unitPrice) }}</td>
                                <td class="td-act">
                                    <button class="rm-btn" @click="removeItem(idx)">×</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="rcpt-items-empty" :style="invoiceType === 'Advance' ? 'min-height: 40px; padding: 12px' : ''">
                        <i class="tim-icons icon-cart" v-if="invoiceType !== 'Advance'"></i>
                        <span v-if="invoiceType === 'Advance'">{{ $t('pos.enterAdvanceAmount') }}</span>
                        <span v-else>{{ $t('pos.clickToAdd') }}</span>
                    </div>
                </div>

                <!-- Rekapitulacija poreza -->
                <div class="rcpt-tax" v-if="taxSummary.length > 0">
                    <div class="tax-row" v-for="t in taxSummary" :key="t.label">
                        <span>{{ $t('pos.receipt.tax') }} {{ t.label }} ({{ t.rate }}%):</span>
                        <span>{{ formatPrice(t.amount) }}</span>
                    </div>
                </div>

                <!-- TOTAL -->
                <div class="rcpt-total" v-if="receiptItems.length > 0">
                    <span>{{ $t('pos.receipt.total') }}</span>
                    <span>{{ formatPrice(receiptTotal) }}</span>
                </div>

                <!-- Avansna uplata info ({{ $t('pos.advances.forFinal') }} račun) -->
                <div class="rcpt-advance-pay" v-if="isFinalByAdvance && receiptItems.length > 0">
                    <div class="adv-pay-row">
                        <span>{{ $t('pos.receipt.payedByAdvance') }}:</span>
                        <strong>{{ formatPrice(advancePayedAmount) }}</strong>
                    </div>
                    <div class="adv-pay-row" v-if="advanceTax > 0">
                        <span>{{ $t('pos.receipt.vatOnAdvance') }}:</span>
                        <strong>{{ formatPrice(advanceTax) }}</strong>
                    </div>
                    <div class="adv-pay-row adv-pay-remaining">
                        <span>{{ $t('pos.receipt.remainingToPay') }}:</span>
                        <strong>{{ formatPrice(remainingAfterAdvance) }}</strong>
                    </div>
                </div>

                <!-- Plaćanje -->
                <div class="rcpt-payment" v-if="receiptItems.length > 0">
                    <label>{{ $t('pos.receipt.paymentMethod') }} <span v-if="isFinalByAdvance">({{ $t('pos.receipt.forRemaining') }})</span></label>
                    <div class="pay-grid">
                        <button v-for="pt in paymentTypes" :key="pt.value"
                            :class="['pay-btn', { active: selectedPaymentType === pt.value }]"
                            @click="selectedPaymentType = pt.value">
                            <i :class="pt.icon"></i>
                            <span>{{ pt.label }}</span>
                        </button>
                    </div>
                </div>

                <!-- Akcije -->
                <div class="rcpt-actions" v-if="receiptItems.length > 0">
                    <button class="btn-clear" @click="clearReceipt()">
                        <i class="tim-icons icon-trash-simple"></i> {{ $t('pos.receipt.clear') }}
                    </button>
                    <button class="btn-fiscal" @click="submitInvoice()" :disabled="submitting">
                        <i class="tim-icons icon-check-2" v-if="!submitting"></i>
                        <i class="tim-icons icon-refresh-02 spin" v-else></i>
                        {{ submitting ? $t('pos.receipt.fiscalizing') : $t('pos.receipt.fiscalize') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- ==================== AVANSI MODAL ==================== -->
        <transition name="modal-fade">
            <div v-if="showAdvancesModal" class="modal-overlay" @click="showAdvancesModal = false">
                <div class="modal-box advances-box" @click.stop>
                    <div class="advm-header">
                        <h3><i class="tim-icons icon-coins"></i> {{ $t('pos.advances.title') }}</h3>
                        <button @click="showAdvancesModal = false" class="hb-close">&times;</button>
                    </div>
                    <div class="advm-body">
                        <div v-if="advanceChains.filter(c => c.status !== 'completed').length === 0" class="advm-empty">
                            {{ $t('pos.advances.noAdvances') }}
                        </div>
                        <div v-for="(chain, ci) in advanceChains.filter(c => c.status !== 'completed')" :key="ci"
                            :class="['chain-card', 'chain-' + chain.status]">
                            <!-- Chain header -->
                            <div class="chain-header">
                                <div class="chain-title">
                                    <span class="chain-num">#{{ ci + 1 }}</span>
                                    <span :class="['chain-status-tag', 'cst-' + chain.status]">
                                        {{ chain.status === 'active' ? $t('pos.advances.status.active') : $t('pos.advances.status.refunded') }}
                                    </span>
                                    <span class="chain-buyer" v-if="chain.buyerId">PIB: {{ chain.buyerId }}</span>
                                    <span class="chain-total">{{ formatPrice(chain.total) }} RSD</span>
                                </div>
                                <div class="chain-actions">
                                    <!-- Aktivan: moze sledeći avans, refundacija, ili konačni (auto-refund + final) -->
                                    <template v-if="chain.status === 'active'">
                                        <button class="adv-btn adv-btn-next" @click="setupNextAdvance(chain)">
                                            <i class="tim-icons icon-simple-add"></i> {{ $t('pos.advances.next') }}
                                        </button>
                                        <button class="adv-btn adv-btn-refund" @click="setupAdvanceRefund(chain)">
                                            <i class="tim-icons icon-refresh-02"></i> {{ $t('pos.advances.refund') }}
                                        </button>
                                        <button class="adv-btn adv-btn-final" @click="setupFinalInvoice(chain)">
                                            <i class="tim-icons icon-check-2"></i> {{ $t('pos.advances.final') }}
                                        </button>
                                    </template>
                                    <!-- Refundiran: samo konačni račun -->
                                    <template v-if="chain.status === 'refunded'">
                                        <button class="adv-btn adv-btn-final" @click="setupFinalInvoice(chain)">
                                            <i class="tim-icons icon-check-2"></i> {{ $t('pos.advances.makeFinal') }}
                                        </button>
                                    </template>
                                </div>
                            </div>
                            <!-- Chain invoices -->
                            <div class="chain-items">
                                <div v-for="(adv, ai) in chain.advances" :key="adv.id" class="chain-item">
                                    <div class="ci-connector" v-if="ai > 0">
                                        <i class="tim-icons icon-minimal-down"></i>
                                    </div>
                                    <div class="ci-row">
                                        <span class="ci-idx">{{ ai + 1 }}.</span>
                                        <span class="ci-pfr">{{ adv.pfrInvoiceNumber || '-' }}</span>
                                        <span class="ci-amount">{{ formatPrice(adv.totalAmount) }}</span>
                                        <span class="ci-date">{{ formatDate(adv.timeStamp) }}</span>
                                    </div>
                                </div>
                                <!-- Prikazi refundaciju ako postoji -->
                                <div v-if="chain.refundInvoice" class="chain-item">
                                    <div class="ci-connector ci-connector-ref">
                                        <i class="tim-icons icon-refresh-02"></i> {{ $t('pos.advances.refundation') }}
                                    </div>
                                    <div class="ci-row ci-row-ref">
                                        <span class="ci-idx">↩</span>
                                        <span class="ci-pfr">{{ chain.refundInvoice.pfrInvoiceNumber || '-' }}</span>
                                        <span class="ci-amount ci-amount-ref">-{{ formatPrice(chain.refundInvoice.totalAmount) }}</span>
                                        <span class="ci-date">{{ formatDate(chain.refundInvoice.timeStamp) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- ==================== RECEIPT RESULT ==================== -->
        <transition name="modal-fade">
            <div v-if="showReceiptModal" class="modal-overlay" @click="showReceiptModal = false">
                <div class="modal-box receipt-box" @click.stop>
                    <div class="rb-header" :class="lastReceipt && lastReceipt.status === 'OK' ? 'rb-ok' : 'rb-err'">
                        <i :class="lastReceipt && lastReceipt.status === 'OK' ? 'tim-icons icon-check-2' : 'tim-icons icon-alert-circle-exc'"></i>
                        {{ lastReceipt && lastReceipt.status === 'OK' ? $t('pos.result.success') : $t('pos.result.error') }}
                    </div>
                    <div class="rb-body" v-if="lastReceipt">
                        <div class="rb-row" v-if="lastReceipt.pfrInvoiceNumber"><span>{{ $t('pos.result.pfrNumber') }}:</span><strong>{{ lastReceipt.pfrInvoiceNumber }}</strong></div>
                        <div class="rb-row" v-if="lastReceipt.pfrDateTime"><span>{{ $t('pos.result.pfrDate') }}:</span><strong>{{ lastReceipt.pfrDateTime }}</strong></div>
                        <div class="rb-row"><span>{{ $t('pos.result.type') }}:</span><strong>{{ invoiceTypeLabel(lastReceipt.invoiceType) }} / {{ txnTypeLabel(lastReceipt.transactionType) }}</strong></div>
                        <div class="rb-row" v-if="lastReceipt.buyerId"><span>{{ $t('pos.buyerId') }}:</span><strong>{{ lastReceipt.buyerId }}</strong></div>
                        <div class="rb-row" v-if="lastReceipt.itemCount"><span>{{ $t('pos.result.items') }}:</span><strong>{{ lastReceipt.itemCount }}</strong></div>
                        <div class="rb-row rb-total"><span>{{ $t('pos.receipt.total') }}:</span><strong>{{ formatPrice(lastReceipt.totalAmount) }} RSD</strong></div>
                        <div class="rb-verification" v-if="lastReceipt.verificationUrl">
                            <a :href="lastReceipt.verificationUrl" target="_blank">
                                <i class="tim-icons icon-link-72"></i> {{ $t('pos.result.verificationLink') }}
                            </a>
                        </div>
                        <div class="rb-journal" v-if="lastReceipt.journal">
                            <pre>{{ lastReceipt.journal }}</pre>
                        </div>
                        <div class="rb-error" v-if="lastReceipt.status === 'ERROR' && lastReceipt.error">
                            <i class="tim-icons icon-alert-circle-exc"></i>
                            {{ lastReceipt.error }}
                        </div>
                    </div>
                    <div class="rb-footer">
                        <button class="btn-fiscal" @click="closeReceipt()">
                            <i class="tim-icons icon-simple-add"></i> {{ $t('pos.receipt.fiscalize') }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- ==================== HISTORY ==================== -->
        <transition name="modal-fade">
            <div v-if="showHistory" class="modal-overlay" @click="showHistory = false">
                <div class="modal-box history-box" @click.stop>
                    <div class="hb-header">
                        <h3><i class="tim-icons icon-paper"></i> {{ $t('pos.history.title') }}</h3>
                        <button @click="showHistory = false" class="hb-close">&times;</button>
                    </div>
                    <!-- Filters -->
                    <div class="hb-filters">
                        <div class="hf-row">
                            <div class="hf-field hf-date">
                                <label>{{ $t('common.from') }}</label>
                                <el-date-picker v-model="histFilter.from" type="datetime" format="dd.MM.yyyy. HH:mm"
                                    :placeholder="$t('common.from')" size="small" style="width:100%"></el-date-picker>
                            </div>
                            <div class="hf-field hf-date">
                                <label>{{ $t('common.to') }}</label>
                                <el-date-picker v-model="histFilter.to" type="datetime" format="dd.MM.yyyy. HH:mm"
                                    :placeholder="$t('common.to')" size="small" style="width:100%"></el-date-picker>
                            </div>
                            <div class="hf-field">
                                <label>{{ $t('pos.invoiceType') }}</label>
                                <select v-model="histFilter.type">
                                    <option value="">{{ $t('invoices.filters.all') }}</option>
                                    <option value="Normal">{{ $t('pos.types.normal') }}</option>
                                    <option value="Advance">{{ $t('pos.types.advance') }}</option>
                                    <option value="Proforma">{{ $t('pos.types.proforma') }}</option>
                                    <option value="Copy">{{ $t('pos.types.copy') }}</option>
                                    <option value="Training">{{ $t('pos.types.training') }}</option>
                                </select>
                            </div>
                            <div class="hf-field">
                                <label>{{ $t('pos.transaction') }}</label>
                                <select v-model="histFilter.txn">
                                    <option value="">{{ $t('invoices.filters.allTypes') }}</option>
                                    <option value="Sale">{{ $t('pos.txn.sale') }}</option>
                                    <option value="Refund">{{ $t('pos.txn.refund') }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="hf-row">
                            <div class="hf-field">
                                <label>{{ $t('invoices.filters.pfrNumber') }}</label>
                                <input type="text" v-model="histFilter.pfrNumber" :placeholder="$t('common.search')">
                            </div>
                            <div class="hf-field">
                                <label>{{ $t('invoices.filters.buyerId') }}</label>
                                <input type="text" v-model="histFilter.buyerId" :placeholder="$t('pos.buyerIdPlaceholder')">
                            </div>
                            <div class="hf-field hf-total">
                                <label>{{ $t('common.results') }}: {{ filteredHistory.length }}</label>
                                <span class="hf-sum">Σ {{ formatPrice(filteredHistoryTotal) }}</span>
                            </div>
                            <div class="hf-field hf-btn-field">
                                <button class="hf-search-btn" @click="loadHistory()">
                                    <i class="tim-icons icon-zoom-split"></i> {{ $t('common.search') }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="hb-body">
                        <table class="hist-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{{ $t('common.date') }}</th>
                                    <th>{{ $t('pos.history.pfrNumber') }}</th>
                                    <th>{{ $t('common.type') }}</th>
                                    <th>{{ $t('pos.history.buyer') }}</th>
                                    <th>{{ $t('common.amount') }}</th>
                                    <th>{{ $t('common.status') }}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(inv, idx) in filteredHistory" :key="inv.id">
                                    <td>{{ idx + 1 }}</td>
                                    <td>{{ formatDate(inv.timeStamp) }}</td>
                                    <td class="mono-cell">{{ inv.pfrInvoiceNumber || '-' }}</td>
                                    <td>
                                        <span class="inv-type-tag">{{ invoiceTypeLabel(inv.invoiceType) }}</span>
                                        <span :class="inv.transactionType === 'Refund' ? 'inv-txn-ref' : ''">
                                            {{ txnTypeLabel(inv.transactionType) }}
                                        </span>
                                    </td>
                                    <td class="mono-cell">{{ inv.buyerId || '-' }}</td>
                                    <td class="mono-cell">{{ formatPrice(inv.totalAmount) }}</td>
                                    <td>
                                        <span :class="['inv-status', inv.status === 'SUCCESS' || inv.status === 'CREATED' ? 'is-ok' : 'is-err']">
                                            {{ inv.status }}
                                        </span>
                                    </td>
                                    <td class="hist-actions">
                                        <template v-if="inv.invoiceType === 'Normal' && inv.transactionType === 'Sale' && (inv.status === 'SUCCESS' || inv.status === 'CREATED')">
                                            <button class="hist-btn hist-btn-refund" @click="openRefundDialog(inv)" :title="$t('pos.history.refundBtn')">
                                                <i class="tim-icons icon-refresh-02"></i>
                                            </button>
                                            <button class="hist-btn hist-btn-storno" @click="doStorno(inv)" :title="$t('pos.history.stornoBtn')">
                                                <i class="tim-icons icon-simple-remove"></i>
                                            </button>
                                        </template>
                                    </td>
                                </tr>
                                <tr v-if="filteredHistory.length === 0">
                                    <td colspan="8" class="hist-empty">{{ $t('pos.history.noInvoices') }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </transition>

        <!-- ==================== REFUND DIALOG ==================== -->
        <transition name="modal-fade">
            <div v-if="refundDialog.show" class="modal-overlay" @click="refundDialog.show = false">
                <div class="modal-box refund-dialog-box" @click.stop>
                    <div class="rfd-header">
                        <h3><i class="tim-icons icon-refresh-02"></i> {{ $t('pos.refundDialog.title') }}</h3>
                        <button @click="refundDialog.show = false" class="hb-close">&times;</button>
                    </div>
                    <div class="rfd-body">
                        <div class="rfd-info">
                            <div><strong>{{ $t('pos.result.pfrNumber') }}:</strong> {{ refundDialog.invoice ? refundDialog.invoice.pfrInvoiceNumber : '' }}</div>
                            <div><strong>{{ $t('common.amount') }}:</strong> {{ refundDialog.invoice ? formatPrice(refundDialog.invoice.totalAmount) : '' }} RSD</div>
                        </div>
                        <div class="rfd-field">
                            <label>{{ $t('pos.refundDialog.buyerLabel') }} *</label>
                            <input type="text" v-model="refundDialog.buyerId" :placeholder="$t('pos.buyerIdPlaceholder')" class="buyer-input">
                        </div>
                        <div class="rfd-actions">
                            <button class="btn-clear" @click="refundDialog.show = false">{{ $t('common.cancel') }}</button>
                            <button class="btn-fiscal" @click="doRefund()" :disabled="!refundDialog.buyerId || refundDialog.submitting">
                                <i class="tim-icons icon-refresh-02"></i>
                                {{ refundDialog.submitting ? $t('pos.refundDialog.sending') : $t('pos.refundDialog.refundBtn') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Toast -->
        <transition name="notif-fade">
            <div v-if="toast.message" :class="['toast-notif', toast.type]" @click="toast.message = ''">
                <i :class="toast.type === 'success' ? 'tim-icons icon-check-2' : 'tim-icons icon-alert-circle-exc'"></i>
                <span>{{ toast.message }}</span>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from '@/util/axiosinstance'
import { DatePicker } from 'element-ui'

export default {
    name: 'FiscalPOS',
    components: { 'el-date-picker': DatePicker },
    data() {
        return {
            loading: false,
            submitting: false,
            esirs: [],
            selectedEsirId: '',
            selectedEsir: null,
            // Catalog
            catalog: [],
            itemSearch: '',
            // Receipt
            receiptItems: [],
            invoiceType: 'Normal',
            transactionType: 'Sale',
            selectedPaymentType: 'Cash',
            buyerId: '',
            refDocNumber: '',
            refDocDt: '',
            // Advance
            advanceDescription: '',
            // Konačni račun po avansu
            isFinalByAdvance: false,
            advancePayedAmount: 0,
            advanceTax: 0,
            // Advance items
            allAdvanceSales: [],
            advanceRefunds: [],
            allNormalSales: [],
            showAdvancesModal: false,
            advanceItems: [
                { label: 'Ђ', name: 'Аванс (Ђ)', taxRate: 20, amount: 0 },
                { label: 'Е', name: 'Аванс (Е)', taxRate: 10, amount: 0 },
                { label: 'Г', name: 'Аванс (Г)', taxRate: 0, amount: 0 },
                { label: 'А', name: 'Аванс (А)', taxRate: 0, amount: 0 }
            ],
            // Invoice types
            // Result
            showReceiptModal: false,
            lastReceipt: null,
            // History
            showHistory: false,
            invoiceHistory: [],
            histFilter: {
                from: new Date(new Date().setHours(0,0,0,0)),
                to: new Date(new Date().setHours(23,59,59,0)),
                type: '', txn: '', pfrNumber: '', buyerId: ''
            },
            filteredHistory: [],
            refundDialog: { show: false, invoice: null, buyerId: '', submitting: false },
            toast: { message: '', type: 'success' }
        }
    },
    computed: {
        invoiceTypes() {
            return [
                { value: 'Normal', label: this.$t('pos.types.normal') },
                { value: 'Advance', label: this.$t('pos.types.advance') },
                { value: 'Proforma', label: this.$t('pos.types.proforma') },
                { value: 'Copy', label: this.$t('pos.types.copy') },
                { value: 'Training', label: this.$t('pos.types.training') }
            ]
        },
        paymentTypes() {
            return [
                { value: 'Cash', label: this.$t('pos.payment.cash'), icon: 'tim-icons icon-money-coins' },
                { value: 'Card', label: this.$t('pos.payment.card'), icon: 'tim-icons icon-credit-card' },
                { value: 'WireTransfer', label: this.$t('pos.payment.wire'), icon: 'tim-icons icon-bank' },
                { value: 'Voucher', label: this.$t('pos.payment.voucher'), icon: 'tim-icons icon-tag' },
                { value: 'MobileMoney', label: this.$t('pos.payment.mobile'), icon: 'tim-icons icon-mobile' },
                { value: 'Other', label: this.$t('pos.payment.other'), icon: 'tim-icons icon-wallet-43' }
            ]
        },
        filteredCatalog() {
            if (!this.itemSearch) return this.catalog
            const s = this.itemSearch.toLowerCase()
            return this.catalog.filter(i =>
                (i.name && i.name.toLowerCase().includes(s)) ||
                (i.gtin && i.gtin.toLowerCase().includes(s))
            )
        },
        receiptTotal() {
            return this.receiptItems.reduce((s, i) => s + (i.quantity * i.unitPrice), 0)
        },
        remainingAfterAdvance() {
            const r = this.receiptTotal - this.advancePayedAmount
            return r > 0 ? r : 0
        },
        filteredHistoryTotal() {
            return this.filteredHistory.reduce((s, inv) => s + Number(inv.totalAmount || 0), 0)
        },
        taxSummary() {
            const taxes = {}
            for (const ri of this.receiptItems) {
                if (ri.label && ri.taxRate > 0) {
                    if (!taxes[ri.label]) taxes[ri.label] = { label: ri.label, rate: ri.taxRate, amount: 0 }
                    const tot = ri.quantity * ri.unitPrice
                    taxes[ri.label].amount += tot * ri.taxRate / (ri.taxRate + 100)
                }
            }
            return Object.values(taxes)
        },
        advanceChains() {
            if (!this.allAdvanceSales.length) return []

            const byPfr = {}
            for (const adv of this.allAdvanceSales) {
                const pfr = adv.pfrInvoiceNumber || adv.invoiceNumber || ''
                if (pfr) byPfr[pfr] = adv
            }

            // Refund mapa: koji PFR avans-prodaje je refundiran, i koji je refund invoice
            const refundedMap = {}
            for (const ref of this.advanceRefunds) {
                if (ref.referentDocumentNumber) {
                    refundedMap[ref.referentDocumentNumber] = ref
                }
            }

            // Grupisanje u lance
            const visited = new Set()
            const chains = []

            for (const adv of this.allAdvanceSales) {
                const pfr = adv.pfrInvoiceNumber || adv.invoiceNumber || ''
                if (visited.has(pfr)) continue

                let root = adv
                const seen = new Set([pfr])
                while (root.referentDocumentNumber && byPfr[root.referentDocumentNumber] && !seen.has(root.referentDocumentNumber)) {
                    seen.add(root.referentDocumentNumber)
                    root = byPfr[root.referentDocumentNumber]
                }

                const chain = [root]
                visited.add(root.pfrInvoiceNumber || root.invoiceNumber || '')

                let changed = true
                while (changed) {
                    changed = false
                    for (const a of this.allAdvanceSales) {
                        const aPfr = a.pfrInvoiceNumber || a.invoiceNumber || ''
                        if (visited.has(aPfr)) continue
                        const lastPfr = chain[chain.length - 1].pfrInvoiceNumber || chain[chain.length - 1].invoiceNumber || ''
                        if (a.referentDocumentNumber === lastPfr) {
                            chain.push(a)
                            visited.add(aPfr)
                            changed = true
                        }
                    }
                }

                const lastInChain = chain[chain.length - 1]
                const lastPfr = lastInChain.pfrInvoiceNumber || lastInChain.invoiceNumber || ''
                const refundInvoice = refundedMap[lastPfr] || null

                // Odredi status
                let status = 'active'
                if (refundInvoice) {
                    // Proveri da li postoji Normal Sale koji referencira ovu refundaciju
                    const refundPfr = refundInvoice.pfrInvoiceNumber || refundInvoice.invoiceNumber || ''
                    const hasFinal = this.allNormalSales.some(inv =>
                        inv.referentDocumentNumber === refundPfr
                    )
                    status = hasFinal ? 'completed' : 'refunded'
                }

                chains.push({
                    advances: chain,
                    status: status,
                    refundInvoice: refundInvoice,
                    lastAdvance: lastInChain,
                    buyerId: chain[0].buyerId || '',
                    total: chain.reduce((s, a) => s + Number(a.totalAmount || 0), 0)
                })
            }

            return chains
        }
    },
    created() {
        this.loadEsirs().then(() => {
            if (this.$route.query.esirId) {
                this.selectedEsirId = this.$route.query.esirId
                this.onEsirSelect()
            }
        })
    },
    methods: {
        loadEsirs() {
            return axios.get('/api/v1/esirs').then(res => { this.esirs = res.data })
        },
        onEsirSelect() {
            this.selectedEsir = this.esirs.find(e => e.id === this.selectedEsirId) || null
            this.catalog = []
            this.clearReceipt()
            if (this.selectedEsirId) {
                this.loadCatalog()
                this.loadActiveAdvances()
            }
        },
        loadCatalog() {
            axios.get('/api/v1/esirItems/' + this.selectedEsirId).then(res => { this.catalog = res.data })
        },

        // Receipt
        addToReceipt(item) {
            const existing = this.receiptItems.find(ri => ri.itemId === item.id)
            if (existing) { existing.quantity += 1; this.recalc(); return }
            this.receiptItems.push({
                itemId: item.id,
                name: item.name,
                quantity: 1,
                unitPrice: item.price || 0,
                unit: item.unit || 'kom',
                label: item.taxLabels || '',
                taxRate: this.getTaxRate(item.taxLabels)
            })
        },
        changeQty(idx, delta) {
            this.receiptItems[idx].quantity += delta
            if (this.receiptItems[idx].quantity <= 0) this.receiptItems.splice(idx, 1)
            this.recalc()
        },
        removeItem(idx) { this.receiptItems.splice(idx, 1) },
        clearReceipt() {
            this.receiptItems = []
            this.invoiceType = 'Normal'
            this.transactionType = 'Sale'
            this.selectedPaymentType = 'Cash'
            this.buyerId = ''
            this.refDocNumber = ''
            this.refDocDt = ''
            this.advanceDescription = ''
            this.isFinalByAdvance = false
            this.advancePayedAmount = 0
            this.advanceTax = 0
            // allAdvanceSales/advanceRefunds/allNormalSales se NE resetuju
            this.advanceItems.forEach(ai => ai.amount = 0)
        },
        recalc() { this.receiptItems = [...this.receiptItems] },
        getTaxRate(label) {
            return { 'Ђ': 20, 'Е': 10, 'Г': 0, 'А': 0 }[label] || 0
        },

        // Advance
        buildAdvanceReceipt() {
            this.receiptItems = this.advanceItems
                .filter(ai => ai.amount > 0)
                .map(ai => ({
                    itemId: 'advance-' + ai.label,
                    name: ai.name,
                    quantity: 1,
                    unitPrice: ai.amount,
                    unit: 'kom',
                    label: ai.label,
                    taxRate: ai.taxRate
                }))
        },

        loadActiveAdvances() {
            if (!this.selectedEsirId) { this.allAdvanceSales = []; this.advanceRefunds = []; this.allNormalSales = []; return }
            axios.get('/api/v1/invoices/esir/' + this.selectedEsirId).then(res => {
                const invoices = res.data

                this.allAdvanceSales = invoices.filter(inv =>
                    inv.invoiceType === 'Advance' &&
                    inv.transactionType === 'Sale' &&
                    (inv.status === 'SUCCESS' || inv.status === 'CREATED')
                )

                this.advanceRefunds = invoices.filter(inv =>
                    inv.invoiceType === 'Advance' &&
                    inv.transactionType === 'Refund' &&
                    (inv.status === 'SUCCESS' || inv.status === 'CREATED')
                )

                // Normal Sale sa referentnim dokumentom (konačni računi)
                this.allNormalSales = invoices.filter(inv =>
                    inv.invoiceType === 'Normal' &&
                    inv.transactionType === 'Sale' &&
                    (inv.status === 'SUCCESS' || inv.status === 'CREATED') &&
                    inv.referentDocumentNumber
                )
            })
        },

        // Novi avans koji se nadovezuje na poslednji u lancu
        setupNextAdvance(chain) {
            const last = chain.lastAdvance
            this.invoiceType = 'Advance'
            this.transactionType = 'Sale'
            this.refDocNumber = last.pfrInvoiceNumber || last.invoiceNumber || ''
            this.refDocDt = this.toDate(last.sdcDateTime || last.pfrDateTime || '')
            this.buyerId = chain.buyerId || ''
            this.receiptItems = []
            this.advanceItems.forEach(ai => ai.amount = 0)
            this.advanceDescription = ''
            this.showAdvancesModal = false
        },

        // Refundacija celog lanca — UKUPAN iznos, referenca na poslednji
        setupAdvanceRefund(chain) {
            const last = chain.lastAdvance
            this.invoiceType = 'Advance'
            this.transactionType = 'Refund'
            this.refDocNumber = last.pfrInvoiceNumber || last.invoiceNumber || ''
            this.refDocDt = this.toDate(last.sdcDateTime || last.pfrDateTime || '')
            this.buyerId = chain.buyerId || ''

            // Saberi iznose po poreskim stopama iz SVIH avansa u lancu
            this.advanceItems.forEach(ai => ai.amount = 0)

            let loadCount = 0
            const total = chain.advances.length

            for (const adv of chain.advances) {
                axios.get('/api/v1/invoice/' + adv.id).then(res => {
                    const items = res.data.items || []
                    for (const item of items) {
                        let label = item.label || ''
                        // Fallback: izvuci iz naziva "Аванс (Ђ)" → "Ђ"
                        if (!label && item.name) {
                            const m = item.name.match(/\(([^)]+)\)/)
                            if (m) label = m[1]
                        }
                        const ai = this.advanceItems.find(a => a.label === label)
                        if (ai) {
                            ai.amount += Number(item.totalAmount) || (Number(item.unitPrice || 0) * Number(item.quantity || 0))
                        }
                    }
                    loadCount++
                    if (loadCount === total) {
                        this.buildAdvanceReceipt()
                        this.advanceDescription = this.$t('pos.advances.refundation') + ' ' + this.refDocNumber
                    }
                })
            }

            this.showAdvancesModal = false
        },

        // Konačni račun — automatski refundira pa postavlja Promet Prodaja
        setupFinalInvoice(chain) {
            if (chain.status === 'refunded') {
                const ref = chain.refundInvoice
                this.invoiceType = 'Normal'
                this.transactionType = 'Sale'
                this.refDocNumber = ref.pfrInvoiceNumber || ref.invoiceNumber || ''
                this.refDocDt = this.toDate(ref.sdcDateTime || ref.pfrDateTime || '')
                this.buyerId = chain.buyerId || ''
                this.receiptItems = []
                this.isFinalByAdvance = true
                this.advancePayedAmount = chain.total || 0
                this.advanceTax = Number(ref.taxAmount) || 0
                this.showAdvancesModal = false
                this.showToast(this.$t('pos.messages.addItemsForFinal'), 'success')
                return
            }

            this.showToast(this.$t('pos.messages.sendingRefund'), 'success')
            this.showAdvancesModal = false

            // Učitaj stavke svih avansa u lancu
            const allItems = []
            let loadCount = 0
            const total = chain.advances.length

            for (const adv of chain.advances) {
                axios.get('/api/v1/invoice/' + adv.id).then(res => {
                    const items = res.data.items || []
                    console.log('Loaded items for advance', adv.id, ':', items.length, JSON.stringify(items.map(i => ({ label: i.label, name: i.name, total: i.totalAmount }))))
                    allItems.push(...items)
                    loadCount++
                    if (loadCount === total) {
                        this.sendAutoRefundThenFinal(chain, allItems)
                    }
                }).catch(err => {
                    console.error('Failed to load advance', adv.id, err)
                    loadCount++
                    if (loadCount === total) {
                        this.sendAutoRefundThenFinal(chain, allItems)
                    }
                })
            }
        },

        // Automatska refundacija + postavljanje konačnog računa
        sendAutoRefundThenFinal(chain, allItems) {
            const last = chain.lastAdvance

            console.log('sendAutoRefundThenFinal - allItems:', JSON.stringify(allItems))

            // Saberi po labelima — dinamički
            const labelTotals = {}
            for (const item of allItems) {
                // Label moze biti u razlicitim formatima
                let label = item.label || ''

                // Ako nema labela, izvuci iz naziva "Аванс (Ђ)" → "Ђ"
                if (!label && item.name) {
                    const m = item.name.match(/\(([^)]+)\)/)
                    if (m) label = m[1]
                }

                if (!label) continue

                if (!labelTotals[label]) {
                    labelTotals[label] = { amount: 0, taxRate: Number(item.taxRate) || 0 }
                }

                // totalAmount moze biti broj, string, ili objekat
                let amount = 0
                if (item.totalAmount != null) {
                    amount = Number(item.totalAmount) || 0
                }
                if (amount === 0 && item.unitPrice != null && item.quantity != null) {
                    amount = Number(item.unitPrice) * Number(item.quantity)
                }
                labelTotals[label].amount += amount
            }

            console.log('sendAutoRefundThenFinal - labelTotals:', JSON.stringify(labelTotals))

            const refundItems = []
            for (const [label, data] of Object.entries(labelTotals)) {
                if (data.amount > 0) {
                    refundItems.push({
                        name: 'Аванс (' + label + ')',
                        quantity: 1,
                        unitPrice: data.amount,
                        label: label,
                        taxRate: data.taxRate
                    })
                }
            }

            if (refundItems.length === 0) {
                console.error('Nema stavki! allItems:', allItems.length, 'labelTotals:', Object.keys(labelTotals))
                this.showToast(this.$t('pos.messages.noRefundItems'), 'error')
                return
            }

            const refundTotal = refundItems.reduce((s, i) => s + i.unitPrice, 0)

            const payload = {
                esirId: this.selectedEsirId,
                invoiceType: 'Advance',
                transactionType: 'Refund',
                buyerId: chain.buyerId || null,
                referentDocumentNumber: last.pfrInvoiceNumber || last.invoiceNumber || '',
                referentDocumentDT: this.dateToStr(last.sdcDateTime || last.pfrDateTime || ''),
                advanceDescription: this.$t('pos.advances.refundation') + ' ' + (last.pfrInvoiceNumber || ''),
                items: refundItems,
                payments: [{ paymentType: 'Cash', amount: refundTotal }]
            }

            this.submitting = true
            axios.post('/api/v1/invoice', payload).then(res => {
                this.submitting = false
                const refundResult = res.data

                if (refundResult.status === 'OK') {
                    this.showToast(this.$t('pos.messages.advanceRefunded'), 'success')

                    this.invoiceType = 'Normal'
                    this.transactionType = 'Sale'
                    this.refDocNumber = refundResult.pfrInvoiceNumber || refundResult.invoiceNumber || ''
                    this.refDocDt = this.toDate(refundResult.pfrDateTime || refundResult.sdcDateTime || '')
                    this.buyerId = chain.buyerId || ''
                    this.receiptItems = []

                    // Popuni advance info {{ $t('pos.advances.forFinal') }} račun
                    this.isFinalByAdvance = true
                    this.advancePayedAmount = chain.total || 0
                    this.advanceTax = Number(refundResult.taxAmount) || 0

                    this.loadActiveAdvances()
                } else {
                    this.showToast(this.$t('pos.messages.refundError') + ': ' + (refundResult.error || ''), 'error')
                }
            }).catch(err => {
                this.submitting = false
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.messages.refundError')
                this.showToast(msg, 'error')
            })
        },

        // Submit
        submitInvoice() {
            if (!this.receiptItems.length) { this.showToast(this.$t('pos.messages.addItems'), 'error'); return }
            if (!this.selectedEsir || !this.selectedEsir.hasCertificate) {
                this.showToast(this.$t('pos.messages.noCert'), 'error'); return
            }
            if (this.transactionType === 'Refund' && !this.refDocNumber) {
                this.showToast(this.$t('pos.messages.refDocRequired'), 'error'); return
            }
            if (this.invoiceType === 'Advance' && !this.advanceDescription.trim()) {
                this.showToast(this.$t('pos.messages.advDescRequired'), 'error'); return
            }
            this.submitting = true

            // Ako je konačni račun po avansu, plaćanje je za preostali iznos
            let paymentAmount = this.receiptTotal
            const payments = []

            if (this.isFinalByAdvance && this.advancePayedAmount > 0) {
                const remaining = this.remainingAfterAdvance
                if (remaining > 0) {
                    payments.push({ paymentType: this.selectedPaymentType, amount: remaining })
                }
                // Čak i ako je remaining 0, V-PFR zahteva barem jedno plaćanje
                if (payments.length === 0) {
                    payments.push({ paymentType: this.selectedPaymentType, amount: 0 })
                }
            } else {
                payments.push({ paymentType: this.selectedPaymentType, amount: this.receiptTotal })
            }

            const payload = {
                esirId: this.selectedEsirId,
                invoiceType: this.invoiceType,
                transactionType: this.transactionType,
                buyerId: this.buyerId || null,
                referentDocumentNumber: this.refDocNumber || null,
                referentDocumentDT: this.dateToStr(this.refDocDt),
                advanceDescription: this.advanceDescription || null,
                payedByAdvance: this.isFinalByAdvance ? this.advancePayedAmount : null,
                advanceTax: this.isFinalByAdvance ? this.advanceTax : null,
                items: this.receiptItems.map(ri => ({
                    name: ri.name, quantity: ri.quantity,
                    unitPrice: ri.unitPrice, label: ri.label, taxRate: ri.taxRate
                })),
                payments: payments
            }
            axios.post('/api/v1/invoice', payload).then(res => {
                this.submitting = false
                this.lastReceipt = res.data
                this.showReceiptModal = true
            }).catch(err => {
                this.submitting = false
                if (err.response && err.response.data && err.response.data.status === 'ERROR') {
                    // V-PFR greska - prikazi u modalu
                    this.lastReceipt = err.response.data
                    this.showReceiptModal = true
                } else {
                    const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                    this.showToast(msg, 'error')
                }
            })
        },
        closeReceipt() {
            this.showReceiptModal = false
            this.clearReceipt()
            this.loadActiveAdvances() // Refreshuj stanje avansa
        },

        // History
        loadHistory() {
            if (!this.selectedEsirId) return
            const params = new URLSearchParams()
            const f = this.histFilter
            if (f.from) params.append('from', (f.from instanceof Date ? f.from.toISOString() : f.from))
            if (f.to) params.append('to', (f.to instanceof Date ? f.to.toISOString() : f.to))
            if (f.type) params.append('invoiceType', f.type)
            if (f.txn) params.append('transactionType', f.txn)
            if (f.pfrNumber) params.append('pfrNumber', f.pfrNumber)
            if (f.buyerId) params.append('buyerId', f.buyerId)

            const qs = params.toString()
            axios.get('/api/v1/invoices/esir/' + this.selectedEsirId + (qs ? '?' + qs : '')).then(res => {
                this.invoiceHistory = res.data
                this.filteredHistory = res.data
            })
        },
        filterHistory() {
            let list = this.invoiceHistory
            const f = this.histFilter

            if (f.type) list = list.filter(inv => inv.invoiceType === f.type)
            if (f.txn) list = list.filter(inv => inv.transactionType === f.txn)
            if (f.pfrNumber) {
                const q = f.pfrNumber.toLowerCase()
                list = list.filter(inv => (inv.pfrInvoiceNumber || '').toLowerCase().includes(q))
            }
            if (f.buyerId) {
                const q = f.buyerId.toLowerCase()
                list = list.filter(inv => (inv.buyerId || '').toLowerCase().includes(q))
            }
            if (f.from) {
                const fromTs = f.from instanceof Date ? f.from.getTime() : new Date(f.from).getTime()
                list = list.filter(inv => this.invoiceTs(inv) >= fromTs)
            }
            if (f.to) {
                const toTs = f.to instanceof Date ? f.to.getTime() : new Date(f.to).getTime()
                list = list.filter(inv => this.invoiceTs(inv) <= toTs)
            }

            this.filteredHistory = list
        },
        invoiceTs(inv) {
            const d = inv.timeStamp
            if (Array.isArray(d)) return new Date(d[0], d[1]-1, d[2], d[3]||0, d[4]||0, d[5]||0).getTime()
            return new Date(d).getTime()
        },
        dateToStr(d) {
            if (!d) return null
            if (typeof d === 'string') return d
            if (d instanceof Date) return d.toISOString()
            if (Array.isArray(d)) {
                // Ebean LocalDateTime: [year, month, day, hour, min, sec, nano]
                const [y, m, day, h, min, s] = d
                return String(y) + '-' + String(m).padStart(2,'0') + '-' + String(day).padStart(2,'0') +
                    'T' + String(h||0).padStart(2,'0') + ':' + String(min||0).padStart(2,'0') + ':' + String(s||0).padStart(2,'0')
            }
            return String(d)
        },
        toDate(d) {
            if (!d) return ''
            if (d instanceof Date) return d
            if (typeof d === 'string') return new Date(d)
            if (Array.isArray(d)) return new Date(d[0], (d[1]||1)-1, d[2]||1, d[3]||0, d[4]||0, d[5]||0)
            return ''
        },

        // Refundacija
        openRefundDialog(inv) {
            this.refundDialog = { show: true, invoice: inv, buyerId: inv.buyerId || '', submitting: false }
        },
        doRefund() {
            const inv = this.refundDialog.invoice
            if (!inv || !this.refundDialog.buyerId) return
            this.refundDialog.submitting = true

            // Ucitaj stavke originalnog racuna
            axios.get('/api/v1/invoice/' + inv.id).then(res => {
                const items = (res.data.items || []).map(it => ({
                    name: it.name, quantity: Number(it.quantity) || 1,
                    unitPrice: Number(it.unitPrice) || 0,
                    label: it.label || '', taxRate: Number(it.taxRate) || 0
                }))
                const total = items.reduce((s, i) => s + i.quantity * i.unitPrice, 0)
                const payments = (res.data.payments || []).map(p => ({
                    paymentType: p.paymentType || 'Cash',
                    amount: Number(p.amount) || 0
                }))

                const payload = {
                    esirId: this.selectedEsirId,
                    invoiceType: inv.invoiceType,
                    transactionType: 'Refund',
                    buyerId: this.refundDialog.buyerId,
                    referentDocumentNumber: inv.pfrInvoiceNumber || inv.invoiceNumber || '',
                    referentDocumentDT: this.dateToStr(inv.sdcDateTime || inv.pfrDateTime || ''),
                    items: items,
                    payments: payments.length ? payments : [{ paymentType: 'Cash', amount: total }]
                }
                axios.post('/api/v1/invoice', payload).then(r => {
                    this.refundDialog.submitting = false
                    if (r.data.status === 'OK') {
                        this.showToast(this.$t('pos.messages.refundSuccess') + ': ' + (r.data.pfrInvoiceNumber || ''), 'success')
                        this.refundDialog.show = false
                        this.loadHistory()
                        this.loadActiveAdvances()
                    } else {
                        this.showToast(this.$t('pos.result.error') + ': ' + (r.data.error || ''), 'error')
                    }
                }).catch(err => {
                    this.refundDialog.submitting = false
                    this.showToast(err.response?.data?.error || this.$t('pos.result.error'), 'error')
                })
            })
        },
        // Storno (refundacija sa sopstvenim PIB-om)
        doStorno(inv) {
            if (!confirm(this.$t('pos.messages.stornoConfirm', { number: inv.pfrInvoiceNumber || '' }))) return
            // Koristi PIB firme iz login-a
            const user = JSON.parse(localStorage.getItem('fiscal_user') || '{}')
            this.refundDialog = { show: false, invoice: inv, buyerId: '', submitting: false }

            // Ucitaj client TIN
            axios.get('/api/v1/user/me').then(meRes => {
                const clientTin = meRes.data.client?.tin || user.clientTin || ''
                this.refundDialog.buyerId = clientTin
                this.refundDialog.invoice = inv
                this.doRefund()
            }).catch(() => {
                this.showToast(this.$t('pos.messages.cantLoadTin'), 'error')
            })
        },

        // Labels
        invoiceTypeLabel(t) { return { Normal: this.$t('pos.types.normal'), Advance: this.$t('pos.types.advance'), Proforma: this.$t('pos.types.proforma'), Copy: this.$t('pos.types.copy'), Training: this.$t('pos.types.training') }[t] || t },
        txnTypeLabel(t) { return t === 'Sale' ? this.$t('pos.txn.sale') : t === 'Refund' ? this.$t('pos.txn.refund') : t },
        formatPrice(v) {
            if (!v && v !== 0) return '0,00'
            return Number(v).toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },
        formatDate(d) {
            if (!d) return '-'
            // Ebean LocalDateTime dolazi kao niz [year, month, day, hour, min, sec, nano]
            if (Array.isArray(d)) {
                const [y, m, day, h, min, s] = d
                return String(day).padStart(2,'0') + '.' + String(m).padStart(2,'0') + '.' + y + '. ' +
                    String(h || 0).padStart(2,'0') + ':' + String(min || 0).padStart(2,'0') + ':' + String(s || 0).padStart(2,'0')
            }
            const dt = new Date(d)
            if (isNaN(dt.getTime())) return String(d)
            return dt.toLocaleString('sr-RS')
        },
        showToast(message, type) {
            if (this._toastTimer) clearTimeout(this._toastTimer)
            this.toast = { message, type }
            this._toastTimer = setTimeout(() => { this.toast = { message: '', type: 'success' } }, 4500)
        }
    },
    watch: {
        showHistory(v) { if (v) this.loadHistory() },
        invoiceType(newVal, oldVal) {
            if (newVal === 'Advance') {
                this.receiptItems = []
                this.advanceItems.forEach(ai => ai.amount = 0)
            } else if (oldVal === 'Advance') {
                this.receiptItems = []
                this.advanceDescription = ''
                if (!this.refDocNumber) {
                    this.refDocNumber = ''
                    this.refDocDt = ''
                }
            }
        }
    }
}
</script>

<style scoped>
* { box-sizing: border-box; }
.pos-page { padding: 0; }

/* Header */
.pos-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 15px; background: #fff; border-radius: 10px;
    border: 1px solid #e0e0e0; margin-bottom: 12px;
}
.ph-left { display: flex; align-items: center; gap: 12px; }
.ph-right { display: flex; gap: 8px; }
.esir-select {
    padding: 8px 12px; border: 2px solid #dee2e6; border-radius: 8px;
    font-size: 14px; font-weight: 600; color: #2c3e50;
    background: #fff; cursor: pointer; min-width: 260px;
}
.esir-select:focus { border-color: #1a73e8; outline: none; }
.esir-cert { font-size: 12px; font-weight: 600; display: flex; align-items: center; gap: 4px; }
.cert-y { color: #28a745; }
.cert-n { color: #dc3545; }
.ph-btn {
    padding: 7px 14px; border: 1px solid #dee2e6; border-radius: 8px;
    background: #fff; font-size: 13px; font-weight: 600; color: #495057;
    cursor: pointer; display: flex; align-items: center; gap: 5px;
}
.ph-btn:hover { background: #f1f5ff; border-color: #1a73e8; color: #1a73e8; }

/* Empty */
.pos-empty { text-align: center; padding: 100px 20px; color: #adb5bd; }
.pos-empty i { font-size: 50px; display: block; margin-bottom: 15px; }
.pos-empty p { font-size: 15px; }

/* Main Layout */
.pos-main { display: grid; grid-template-columns: 1fr 520px; gap: 12px; }
.pos-main-full { grid-template-columns: 1fr; max-width: 560px; margin: 0 auto; }

/* Advance panel */
.advance-panel {
    padding: 10px 12px; border-bottom: 1px solid #e9ecef;
    background: #f0f7ff;
}
.adv-info {
    display: flex; align-items: center; gap: 8px;
    font-size: 12px; color: #1a73e8; font-weight: 500;
    margin-bottom: 8px;
}
.adv-info i { font-size: 16px; }
.adv-items { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 8px; }
.adv-item {
    display: flex; align-items: center; gap: 6px;
    background: #fff; padding: 6px 8px; border-radius: 8px;
    border: 1px solid #dee2e6;
}
.adv-label { font-size: 13px; font-weight: 600; color: #2c3e50; white-space: nowrap; min-width: 85px; }
.adv-input {
    flex: 1; padding: 6px 8px; border: 1px solid #dee2e6;
    border-radius: 6px; font-size: 14px; font-family: monospace;
    text-align: right; min-width: 0;
}
.adv-input:focus { border-color: #1a73e8; outline: none; }
.adv-desc { }
.adv-desc label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #856404; margin-bottom: 3px; }
.adv-desc small { font-weight: 400; }
.adv-desc textarea { width: 100%; resize: vertical; font-family: inherit; }

/* Ref hint */
.ref-hint {
    margin-top: 6px; font-size: 12px; color: #1a73e8;
    display: flex; align-items: center; gap: 5px;
}
.ref-hint i { font-size: 14px; }

/* Catalog */
.pos-catalog {
    background: #fff; border-radius: 10px;
    border: 1px solid #e0e0e0; display: flex; flex-direction: column;
    max-height: calc(100vh - 180px); overflow: hidden;
}
.catalog-search { padding: 10px 12px; border-bottom: 1px solid #e9ecef; }
.cat-input {
    width: 100%; padding: 9px 12px; border: 1px solid #dee2e6;
    border-radius: 8px; font-size: 14px; color: #2c3e50;
}
.cat-input:focus { border-color: #1a73e8; outline: none; }
.catalog-grid {
    flex: 1; overflow-y: auto; padding: 8px;
    display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 8px; align-content: start;
}
.cat-item {
    padding: 12px; border: 1px solid #e9ecef; border-radius: 8px;
    cursor: pointer; transition: all 0.12s; user-select: none;
}
.cat-item:hover { background: #eef5ff; border-color: #1a73e8; transform: scale(1.02); }
.cat-item:active { transform: scale(0.97); background: #dbeaff; }
.ci-name { font-size: 13px; font-weight: 600; color: #2c3e50; margin-bottom: 4px; }
.ci-bottom { display: flex; align-items: center; gap: 6px; }
.ci-price { font-family: monospace; font-size: 13px; color: #28a745; font-weight: 700; }
.ci-tax { background: #fff3cd; color: #856404; padding: 0 5px; border-radius: 3px; font-size: 10px; font-weight: 700; }
.ci-unit { font-size: 11px; color: #6c757d; }
.catalog-empty { grid-column: 1/-1; text-align: center; padding: 40px; color: #adb5bd; font-size: 14px; }

/* Receipt */
.pos-receipt {
    background: #fff; border-radius: 10px;
    border: 1px solid #e0e0e0; display: flex; flex-direction: column;
    max-height: calc(100vh - 180px); overflow-y: auto;
}
.pos-main-full .pos-receipt {
    max-height: none; overflow: visible;
}

/* Types */
.rcpt-types { padding: 10px 12px; border-bottom: 1px solid #e9ecef; display: flex; gap: 10px; }
.rcpt-type-group { flex: 1; }
.rcpt-type-group label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #6c757d; margin-bottom: 4px; }
.type-btns { display: flex; gap: 3px; }
.tbtn {
    flex: 1; padding: 5px 2px; border: 1px solid #dee2e6; border-radius: 5px;
    background: #fff; font-size: 11px; font-weight: 600; color: #495057;
    cursor: pointer; text-align: center; transition: all 0.15s;
}
.tbtn:hover { border-color: #1a73e8; color: #1a73e8; }
.tbtn.active { background: #1a73e8; border-color: #1a73e8; color: #fff; }
.tbtn-ref.active { background: #dc3545; border-color: #dc3545; }

/* Advances bar */
.advances-bar {
    padding: 8px 12px; border-bottom: 1px solid #e9ecef; background: #fff8e1;
}
.adv-open-btn {
    width: 100%; padding: 8px 12px; border: 1px dashed #ffc107;
    border-radius: 8px; background: transparent;
    font-size: 13px; font-weight: 600; color: #856404;
    cursor: pointer; display: flex; align-items: center; gap: 8px;
    transition: all 0.15s;
}
.adv-open-btn:hover { background: #fff3cd; border-style: solid; }
.adv-open-btn .tim-icons:last-child { margin-left: auto; }

/* Advances modal */
.advances-box { max-width: 700px; }
.advm-header {
    padding: 15px 20px; background: linear-gradient(135deg, #f0ad4e, #ec971f);
    display: flex; justify-content: space-between; align-items: center;
}
.advm-header h3 { margin: 0; color: #fff; font-size: 16px; font-weight: 700; display: flex; align-items: center; gap: 8px; }
.advm-body { padding: 15px; max-height: 500px; overflow-y: auto; }
.advm-empty { text-align: center; padding: 30px; color: #adb5bd; font-size: 14px; }

/* Chain card */
.chain-card {
    margin-bottom: 12px; border: 1px solid #e9ecef;
    border-radius: 10px; overflow: hidden; background: #fff;
}
.chain-card.chain-refunded {
    border-color: #28a745; border-left: 4px solid #28a745;
}
.chain-card.chain-active {
    border-color: #ffc107; border-left: 4px solid #ffc107;
}
.chain-status-tag {
    padding: 2px 8px; border-radius: 4px;
    font-size: 10px; font-weight: 700; text-transform: uppercase;
}
.cst-active { background: #fff3cd; color: #856404; }
.cst-refunded { background: #d4edda; color: #155724; }
.cst-completed { background: #e2e3e5; color: #383d41; }
.chain-header {
    padding: 12px 14px; background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    display: flex; justify-content: space-between;
    align-items: center; flex-wrap: wrap; gap: 8px;
}
.chain-title { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.chain-num {
    background: #2c3e50; color: #fff; padding: 2px 8px;
    border-radius: 5px; font-size: 12px; font-weight: 700;
}
.chain-buyer { font-size: 12px; color: #1a73e8; font-weight: 600; }
.chain-total { font-size: 13px; font-weight: 700; color: #28a745; font-family: monospace; }
.chain-actions { display: flex; gap: 5px; }
.chain-items { padding: 8px 14px; }
.chain-item { position: relative; }
.ci-connector {
    text-align: center; color: #adb5bd; font-size: 12px;
    padding: 2px 0;
}
.ci-row {
    display: flex; align-items: center; gap: 10px;
    padding: 6px 10px; background: #f8f9fa;
    border-radius: 6px; margin-bottom: 2px;
}
.ci-idx { font-size: 11px; color: #6c757d; font-weight: 600; min-width: 18px; }
.ci-pfr { font-family: monospace; font-size: 12px; font-weight: 600; color: #2c3e50; }
.ci-amount { font-family: monospace; font-size: 12px; font-weight: 700; color: #28a745; }
.ci-date { font-size: 11px; color: #6c757d; margin-left: auto; }
.ci-connector-ref { color: #dc3545; font-size: 11px; font-weight: 600; }
.ci-row-ref { background: #fff5f5; border: 1px solid #f8d7da; }
.ci-amount-ref { color: #dc3545 !important; }

/* Advance action buttons (shared) */
.adv-btn {
    padding: 5px 10px; border: none; border-radius: 5px;
    font-size: 11px; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; gap: 4px; transition: all 0.15s;
}
.adv-btn i { font-size: 12px; }
.adv-btn-next { background: #e7f3ff; color: #1a73e8; }
.adv-btn-next:hover { background: #1a73e8; color: #fff; }
.adv-btn-refund { background: #f8d7da; color: #dc3545; }
.adv-btn-refund:hover { background: #dc3545; color: #fff; }
.adv-btn-final { background: #d4edda; color: #155724; }
.adv-btn-final:hover { background: #28a745; color: #fff; }

/* Buyer */
.rcpt-buyer { padding: 8px 12px; border-bottom: 1px solid #e9ecef; }
.rcpt-buyer label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #6c757d; margin-bottom: 3px; }
.rcpt-buyer small { font-weight: 400; }
.buyer-input {
    width: 100%; padding: 7px 10px; border: 1px solid #dee2e6;
    border-radius: 6px; font-size: 13px; color: #2c3e50;
}
.buyer-input:focus { border-color: #1a73e8; outline: none; }

/* Ref document */
.rcpt-ref { padding: 8px 12px; border-bottom: 1px solid #e9ecef; background: #fff8f0; }
.ref-row { display: flex; gap: 8px; }
.ref-field { flex: 1; }
.ref-field label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #856404; margin-bottom: 3px; }
.ref-date .el-date-editor { width: 100% !important; }

/* Items */
.rcpt-items { flex: 1; overflow-y: auto; min-height: 120px; }
.pos-main-full .rcpt-items { min-height: 40px; }
.rcpt-table { width: 100%; border-collapse: collapse; }
.rcpt-table th {
    padding: 6px 8px; font-size: 10px; font-weight: 700;
    text-transform: uppercase; color: #6c757d; background: #f8f9fa;
    border-bottom: 1px solid #e9ecef; text-align: left;
}
.th-name { width: auto; }
.th-qty { width: 100px; text-align: center; }
.th-price { width: 85px; text-align: right; }
.th-total { width: 85px; text-align: right; }
.th-act { width: 30px; }
.rcpt-table td { padding: 6px 8px; border-bottom: 1px solid #f1f3f5; vertical-align: middle; }
.td-name span { font-size: 13px; font-weight: 600; color: #2c3e50; display: block; }
.td-name small { font-size: 10px; color: #6c757d; }
.td-qty { text-align: center; }
.qty-wrap { display: flex; align-items: center; justify-content: center; gap: 2px; }
.qb {
    width: 24px; height: 24px; border: 1px solid #dee2e6; border-radius: 4px;
    background: #fff; font-size: 15px; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; justify-content: center; color: #495057;
}
.qb:hover { background: #eef5ff; border-color: #1a73e8; }
.qi {
    width: 40px; text-align: center; border: 1px solid #dee2e6;
    border-radius: 4px; padding: 2px; font-size: 13px; font-weight: 600;
}
.qi:focus { border-color: #1a73e8; outline: none; }
.td-price { text-align: right; }
.pi {
    width: 75px; text-align: right; border: 1px solid #dee2e6;
    border-radius: 4px; padding: 2px 4px; font-size: 13px; font-family: monospace;
}
.pi:focus { border-color: #1a73e8; outline: none; }
.td-total { text-align: right; font-family: monospace; font-size: 13px; font-weight: 600; color: #2c3e50; }
.td-act { text-align: center; }
.rm-btn {
    width: 22px; height: 22px; border: none; border-radius: 4px;
    background: #f8d7da; color: #721c24; font-size: 14px; font-weight: 700;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
}
.rm-btn:hover { background: #dc3545; color: #fff; }
.rcpt-items-empty {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 40px 20px; color: #adb5bd; gap: 8px;
}
.rcpt-items-empty i { font-size: 30px; }
.rcpt-items-empty span { font-size: 13px; }

/* Tax */
.rcpt-tax { padding: 6px 12px; background: #f8f9fa; border-top: 1px solid #e9ecef; }
.tax-row { display: flex; justify-content: space-between; font-size: 12px; color: #495057; padding: 2px 0; }

/* Total */
.rcpt-total {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px; background: #2c3e50; color: #fff;
    font-size: 22px; font-weight: 800; font-family: monospace;
}

/* Payment */
.rcpt-payment { padding: 10px 12px; border-top: 1px solid #e9ecef; }

/* Advance payment info */
.rcpt-advance-pay {
    padding: 10px 12px; background: #f0f7ff;
    border-top: 1px solid #bee5eb;
}
.adv-pay-row {
    display: flex; justify-content: space-between; align-items: center;
    font-size: 13px; padding: 3px 0; color: #2c3e50;
}
.adv-pay-row strong { font-family: monospace; }
.adv-pay-remaining {
    margin-top: 4px; padding-top: 6px;
    border-top: 1px dashed #1a73e8;
    font-weight: 700; color: #1a73e8; font-size: 14px;
}
.rcpt-payment > label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #6c757d; margin-bottom: 6px; }
.pay-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 5px; }
.pay-btn {
    padding: 8px 4px; border: 1px solid #dee2e6; border-radius: 6px;
    background: #fff; cursor: pointer; text-align: center;
    transition: all 0.15s; display: flex; flex-direction: column;
    align-items: center; gap: 2px;
}
.pay-btn i { font-size: 16px; color: #6c757d; }
.pay-btn span { font-size: 10px; font-weight: 600; color: #495057; }
.pay-btn:hover { border-color: #1a73e8; }
.pay-btn.active { background: #1a73e8; border-color: #1a73e8; }
.pay-btn.active i, .pay-btn.active span { color: #fff; }

/* Actions */
.rcpt-actions { padding: 10px 12px; display: flex; gap: 8px; border-top: 1px solid #e9ecef; }
.btn-clear {
    padding: 10px 16px; border: 1px solid #dee2e6; border-radius: 8px;
    background: #fff; font-size: 13px; font-weight: 600; color: #dc3545;
    cursor: pointer; display: flex; align-items: center; gap: 5px;
}
.btn-clear:hover { background: #f8d7da; }
.btn-fiscal {
    flex: 1; padding: 12px; border: none; border-radius: 8px;
    background: #28a745; color: #fff; font-size: 16px; font-weight: 800;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
    letter-spacing: 1px; transition: background 0.2s;
}
.btn-fiscal:hover { background: #1e7e34; }
.btn-fiscal:disabled { background: #6c757d; cursor: not-allowed; }
.btn-fiscal i { font-size: 18px; }

/* Receipt Result Modal */
.modal-overlay {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.5); display: flex;
    align-items: center; justify-content: center;
    z-index: 99999; backdrop-filter: blur(3px);
}
.modal-box {
    background: #fff; border-radius: 14px;
    max-width: 420px; width: 95%; box-shadow: 0 20px 60px rgba(0,0,0,0.25);
    overflow: hidden;
}
.rb-header {
    padding: 20px; text-align: center; font-size: 20px;
    font-weight: 800; display: flex; align-items: center;
    justify-content: center; gap: 10px; color: #fff;
}
.rb-ok { background: #28a745; }
.rb-err { background: #dc3545; }
.rb-body { padding: 20px; }
.rb-row {
    display: flex; justify-content: space-between;
    padding: 6px 0; font-size: 14px; color: #495057;
    border-bottom: 1px dashed #e9ecef;
}
.rb-row strong { color: #2c3e50; }
.rb-total {
    border-bottom: none; border-top: 2px solid #2c3e50;
    margin-top: 8px; padding-top: 10px;
    font-size: 18px; font-weight: 800;
}
.rb-total strong { color: #28a745; }
.rb-error {
    margin-top: 12px; padding: 10px 14px;
    background: #f8d7da; border-radius: 8px;
    border: 1px solid #f5c6cb;
    color: #721c24; font-size: 13px;
    display: flex; align-items: flex-start; gap: 8px;
}
.rb-error i { font-size: 16px; flex-shrink: 0; margin-top: 1px; }
.rb-verification {
    margin-top: 12px; text-align: center;
}
.rb-verification a {
    color: #1a73e8; font-size: 14px; font-weight: 600;
    text-decoration: none; display: inline-flex; align-items: center; gap: 5px;
}
.rb-verification a:hover { text-decoration: underline; }
.rb-journal {
    margin-top: 12px; background: #f8f9fa; border: 1px solid #e9ecef;
    border-radius: 8px; padding: 12px; max-height: 250px; overflow-y: auto;
}
.rb-journal pre {
    margin: 0; font-family: 'Courier New', monospace;
    font-size: 11px; line-height: 1.4; color: #2c3e50;
    white-space: pre-wrap; word-wrap: break-word;
}
.rb-footer { padding: 15px 20px; background: #f8f9fa; }
.rb-footer .btn-fiscal { width: 100%; }

/* History Modal */
.history-box { max-width: 950px; }
.hb-header {
    padding: 15px 20px; background: #2c3e50;
    display: flex; justify-content: space-between; align-items: center;
}
.hb-header h3 { margin: 0; color: #fff; font-size: 16px; display: flex; align-items: center; gap: 8px; }
.hb-close { background: none; border: none; color: #fff; font-size: 24px; cursor: pointer; }
/* Filters */
.hb-filters { padding: 10px 15px; background: #f8f9fa; border-bottom: 1px solid #e9ecef; }
.hf-row { display: flex; gap: 8px; margin-bottom: 6px; flex-wrap: wrap; }
.hf-row:last-child { margin-bottom: 0; }
.hf-field { flex: 1; min-width: 120px; }
.hf-field label { display: block; font-size: 9px; font-weight: 700; text-transform: uppercase; color: #6c757d; margin-bottom: 2px; }
.hf-field input, .hf-field select {
    width: 100%; padding: 5px 8px; border: 1px solid #dee2e6;
    border-radius: 6px; font-size: 12px; color: #2c3e50;
}
.hf-field input:focus, .hf-field select:focus { border-color: #1a73e8; outline: none; }
.hf-total { display: flex; flex-direction: column; justify-content: flex-end; align-items: flex-end; }
.hf-sum { font-family: monospace; font-size: 14px; font-weight: 700; color: #28a745; }
.hf-btn-field { display: flex; align-items: flex-end; }
.hf-search-btn {
    padding: 6px 16px; border: none; background: #1a73e8; color: #fff;
    border-radius: 6px; font-size: 12px; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; gap: 5px; white-space: nowrap; transition: all 0.15s;
}
.hf-search-btn:hover { background: #1557b0; }
.hf-date .el-date-editor { width: 100% !important; }
.hb-body { max-height: 450px; overflow-y: auto; }
.hist-table { width: 100%; border-collapse: collapse; }
.hist-table th {
    padding: 6px 10px; font-size: 10px; font-weight: 700;
    text-transform: uppercase; color: #6c757d; background: #f8f9fa;
    border-bottom: 1px solid #e9ecef; text-align: left; white-space: nowrap;
}
.hist-table td { padding: 6px 10px; border-bottom: 1px solid #f1f3f5; font-size: 12px; color: #2c3e50; }
.hist-table tbody tr:hover { background: #f1f5ff; }
.mono-cell { font-family: monospace; font-size: 11px; }
.inv-status { padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 700; }
.is-ok { background: #d4edda; color: #155724; }
.is-err { background: #f8d7da; color: #721c24; }
.inv-type-tag { font-weight: 600; }
.inv-txn-ref { color: #dc3545; font-weight: 600; }
.hist-empty { text-align: center; padding: 30px !important; color: #adb5bd; }
.hist-actions { white-space: nowrap; }
.hist-btn {
    padding: 4px 6px; border: none; border-radius: 4px; cursor: pointer;
    font-size: 12px; margin-left: 3px; transition: all 0.15s;
}
.hist-btn-refund { background: #e7f3ff; color: #1a73e8; }
.hist-btn-refund:hover { background: #1a73e8; color: #fff; }
.hist-btn-storno { background: #f8d7da; color: #dc3545; }
.hist-btn-storno:hover { background: #dc3545; color: #fff; }

/* Refund dialog */
.refund-dialog-box { max-width: 420px; }
.rfd-header {
    padding: 15px 20px; background: #1a73e8;
    display: flex; justify-content: space-between; align-items: center;
}
.rfd-header h3 { margin: 0; color: #fff; font-size: 16px; display: flex; align-items: center; gap: 8px; }
.rfd-body { padding: 20px; }
.rfd-info { margin-bottom: 15px; background: #f8f9fa; padding: 10px 12px; border-radius: 8px; font-size: 13px; line-height: 1.8; }
.rfd-field { margin-bottom: 15px; }
.rfd-field label { display: block; font-size: 11px; font-weight: 700; color: #495057; margin-bottom: 4px; }
.rfd-actions { display: flex; gap: 8px; justify-content: flex-end; }

/* Loader */
.pos-loader {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255,255,255,0.7); display: flex;
    align-items: center; justify-content: center; z-index: 9999;
}
.loader {
    width: 36px; height: 36px; border: 4px solid #e9ecef;
    border-top-color: #1a73e8; border-radius: 50%; animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.spin { animation: spin 1s linear infinite; }

/* Transitions */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.25s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }

/* Toast */
.toast-notif {
    position: fixed; top: 80px; left: 50%; transform: translateX(-50%);
    padding: 16px 32px; border-radius: 12px;
    display: flex; align-items: center; gap: 12px;
    font-size: 16px; font-weight: 600; z-index: 100000;
    cursor: pointer; box-shadow: 0 8px 30px rgba(0,0,0,0.25); color: #fff;
    max-width: 90vw; text-align: center;
}
.toast-notif i { font-size: 22px; flex-shrink: 0; }
.toast-notif.success { background: #28a745; }
.toast-notif.error { background: #dc3545; }
.notif-fade-enter-active, .notif-fade-leave-active { transition: all 0.35s ease; }
.notif-fade-enter, .notif-fade-leave-to { opacity: 0; transform: translateX(-50%) translateY(-30px); }

/* Responsive */
@media (max-width: 900px) {
    .pos-main { grid-template-columns: 1fr; }
    .pos-main-full { max-width: 100%; }
    .pos-receipt { max-height: none; }
    .pos-catalog { max-height: 350px; }
    .catalog-grid { grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); }
    .pay-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>